import {
  type KnnSearchResponse,
  type MsearchMultisearchBody,
} from '@elastic/elasticsearch/lib/api/types';
import {
  type IContentListAdvancedFilter,
  type IContentListFilter,
} from 'features/content/interfaces';
import React from 'react';
import { type ActionDelegate } from 'store';
import { useContentStore } from 'store/slices';
import { type IContentProps, type IContentState } from 'store/slices/content';
import {
  type IContentListModel,
  type IContentModel,
  type IContentTopicModel,
  type INotificationInstanceModel,
  useApiEditorContents,
} from 'tno-core';

import { useAjaxWrapper } from '..';

interface IContentController {
  findContentWithElasticsearch: (
    filter: MsearchMultisearchBody,
    includeUnpublishedContent: boolean,
  ) => Promise<KnnSearchResponse<IContentModel>>;
  getContent: (id: number) => Promise<IContentModel | undefined>;
  addContent: (content: IContentModel) => Promise<IContentModel>;
  updateContent: (content: IContentModel) => Promise<IContentModel>;
  updateContentList: (content: IContentListModel) => Promise<IContentModel[]>;
  updateContentTopics: (id: number, topics?: IContentTopicModel[]) => Promise<IContentTopicModel[]>;
  resetContentTopicScore: (id: number, topicId: number) => Promise<IContentTopicModel[]>;
  deleteContent: (content: IContentModel) => Promise<IContentModel>;
  publishContent: (content: IContentModel) => Promise<IContentModel>;
  unpublishContent: (content: IContentModel) => Promise<IContentModel>;
  upload: (content: IContentModel, file: File) => Promise<IContentModel>;
  download: (id: number, fileName: string) => Promise<unknown>;
  attach: (contentId: number, locationId: number, path: string) => Promise<IContentModel>;
  storeFilter: (filter: IContentListFilter | ActionDelegate<IContentListFilter>) => void;
  stream: (path: string) => Promise<string>;
  storeFilterAdvanced: (
    filter: IContentListAdvancedFilter | ActionDelegate<IContentListAdvancedFilter>,
  ) => void;
  storeFilterPaper: (filter: IContentListFilter | ActionDelegate<IContentListFilter>) => void;
  storeFilterPaperAdvanced: (
    filter: IContentListAdvancedFilter | ActionDelegate<IContentListAdvancedFilter>,
  ) => void;
  getNotificationsFor: (id: number) => Promise<INotificationInstanceModel[]>;
}

export const useContent = (props?: IContentProps): [IContentState, IContentController] => {
  const [state, actions] = useContentStore(props);
  const dispatch = useAjaxWrapper();
  const api = useApiEditorContents();

  const controller = React.useMemo(() => {
    return {
      findContentWithElasticsearch: async (
        filter: MsearchMultisearchBody,
        includeUnpublishedContent: boolean,
      ) => {
        const response = await dispatch(
          'find-contents-with-elasticsearch',
          async () => await api.findContentWithElasticsearch(filter, includeUnpublishedContent),
        );
        return response.data as KnnSearchResponse<IContentModel>;
      },
      getContent: async (id: number) => {
        const response = await dispatch(
          'get-content',
          async () => await api.getContent(id),
          'content',
        );
        return response.status === 204 ? undefined : (response.data as IContentModel | undefined);
      },
      addContent: async (content: IContentModel) => {
        const response = await dispatch(
          'add-content',
          async () => await api.addContent(content),
          'content',
        );
        return response.data as IContentModel;
      },
      updateContent: async (content: IContentModel) => {
        const response = await dispatch(
          'update-content',
          async () => await api.updateContent(content),
          'content',
        );
        return response.data as IContentModel;
      },
      updateContentList: async (content: IContentListModel) => {
        const response = await dispatch(
          'update-content-list',
          async () => await api.updateContentList(content),
          'content',
        );
        return response.data as IContentModel[];
      },
      updateContentTopics: async (id: number, topics?: IContentTopicModel[]) => {
        const response = await dispatch(
          'update-content-topics',
          async () => await api.updateContentTopics(id, topics ?? []),
          'content',
        );
        return response.data as IContentTopicModel[];
      },
      resetContentTopicScore: async (id: number, topicId: number) => {
        const response = await dispatch(
          'reset-content-topic-score',
          async () => await api.resetContentTopicScore(id, topicId),
          'content',
        );
        return response.data as IContentTopicModel[];
      },
      deleteContent: async (content: IContentModel) => {
        const response = await dispatch(
          'delete-content',
          async () => await api.deleteContent(content),
          'content',
        );
        return response.data as IContentModel;
      },
      publishContent: async (content: IContentModel) => {
        return (
          await dispatch(
            'publish-content',
            async () => await api.publishContent(content),
            'content',
          )
        ).data as IContentModel;
      },
      unpublishContent: async (content: IContentModel) => {
        return (
          await dispatch(
            'unpublish-content',
            async () => await api.unpublishContent(content),
            'content',
          )
        ).data as IContentModel;
      },
      upload: async (content: IContentModel, file: File) => {
        return (
          await dispatch('upload-content', async () => await api.upload(content, file), 'content')
        ).data as IContentModel;
      },
      download: async (id: number, fileName: string) => {
        return (
          await dispatch(
            'download-content',
            async () => await api.download(id, fileName),
            'content',
          )
        ).data;
      },
      attach: async (contentId: number, locationId: number, path: string) => {
        return (
          await dispatch<IContentModel>(
            'attach-content',
            async () => await api.attach(contentId, locationId, path),
            'content',
          )
        ).data as IContentModel;
      },
      stream: async (path: string) => {
        return (await dispatch('stream-content', async () => await api.stream(path), 'content'))
          .data as string;
      },
      getNotificationsFor: async (id: number) => {
        return (
          await dispatch(
            'content-notifications',
            async () => await api.getNotificationsFor(id),
            'content',
          )
        ).data as INotificationInstanceModel[];
      },
      storeFilter: actions.storeFilter,
      storeFilterAdvanced: actions.storeFilterAdvanced,
      storeFilterPaper: actions.storeFilterPaper,
      storeFilterPaperAdvanced: actions.storeFilterPaperAdvanced,
    };
  }, [actions, api, dispatch]);

  return [state, controller];
};
